'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

type Loadings = {
  [key: string]: boolean | Record<string, boolean> | undefined
  removeLineItems?: Record<string, boolean>
  addLineItems?: Record<string, boolean>
  changeLineItems?: Record<string, boolean>
  isAddingLineItems?: boolean
  isRemovingLineItems?: boolean
  isChangingLineItems?: boolean
  isTokenizingCreditCard?: boolean
}

export const LoadingsContext = createContext({
  loadings: {} as unknown as Loadings,
  setLoading: (_value: Loadings) => {},
  resetLoadings: () => {},
})

const initialState: Loadings = {
  removeLineItems: {},
  addLineItems: {},
  isAddingLineItems: false,
  isRemovingLineItems: false,
  isChangingLineItems: false,
  isTokenizingCreditCard: false,
}

export function LoadingsProvider({ children }: { children: React.ReactNode }) {
  const [loadings, set] = useState(initialState)

  const setLoading = useCallback((value: Loadings) => {
    set((prev) => ({
      ...prev,
      ...value,
      addLineItems: { ...prev.addLineItems, ...value.addLineItems },
      removeLineItems: { ...prev.removeLineItems, ...value.removeLineItems },
      changeLineItems: { ...prev.changeLineItems, ...value.changeLineItems },
    }))
  }, [])

  const resetLoadings = useCallback(() => {
    set(initialState)
  }, [])

  const isRemovingLineItems = useMemo(
    () => Object.values(loadings.removeLineItems ?? []).some(Boolean),
    [loadings.removeLineItems]
  )
  const isAddingLineItems = useMemo(
    () => Object.values(loadings.addLineItems ?? []).some(Boolean),
    [loadings.addLineItems]
  )
  const isChangingLineItems = useMemo(
    () => Object.values(loadings.changeLineItems ?? []).some(Boolean),
    [loadings.changeLineItems]
  )

  const value = useMemo(() => {
    return {
      loadings: { ...loadings, isAddingLineItems, isRemovingLineItems, isChangingLineItems },
      setLoading,
      resetLoadings,
    }
  }, [
    isAddingLineItems,
    isRemovingLineItems,
    isChangingLineItems,
    loadings,
    resetLoadings,
    setLoading,
  ])

  return <LoadingsContext.Provider value={value}>{children}</LoadingsContext.Provider>
}

export const useLoadings = () => useContext(LoadingsContext)
